// ============================================================================
// DIRECT GRAPHQL HOOKS FOR EMPLOYEES (app_users)
// ----------------------------------------------------------------------------
// Mirrors the pattern used by ticket-hooks and task GraphQL hooks.
// ============================================================================

import { useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import { gql } from "@apollo/client";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import {
  GetUserssDocument,
  CreateUsersDocument,
  UpdateUsersDocument,
  DeleteUsersDocument,
  GetUserssQuery,
  CreateUsersMutation,
  UpdateUsersMutation,
  DeleteUsersMutation,
  App_Users,
  App_UsersInsertInput,
  App_UsersUpdateInput,
} from "@/src/gql/graphql";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";

export type Employee = {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  role_id?: string | null;
  is_active?: boolean | null;
  department?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

const GET_EMPLOYEE_DETAIL_LITE = gql`
  query GetEmployeeDetailLite($id: UUID!) {
    app_usersCollection(filter: { id: { eq: $id } }) {
      edges {
        node {
          id
          email
          first_name
          last_name
          role_id
          is_active
          created_at
          updated_at
        }
      }
    }
  }
`;

type GetEmployeeDetailLiteQuery = {
  app_usersCollection: {
    edges: Array<{
      node: App_Users | null;
    } | null> | null;
  } | null;
};

export const useEmployeeHooks = (): EntityHooks<App_Users> => {
  return useMemo(
    (): EntityHooks<App_Users> => ({
      queryDocument: GetUserssDocument,
      usePaginated: (params?: Record<string, unknown>) =>
        // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
        usePaginatedCollection(
          GetUserssDocument,
          params,
          (data: GetUserssQuery) => data.app_usersCollection,
          (node) => node as App_Users
        ),
      useInfinite: (_params?: Record<string, unknown>) => {
        // TODO: Implement infinite query
        return {
          data: undefined,
          isLoading: false,
          error: null,
          refetch: () => {},
          hasNextPage: false,
          isFetchingNextPage: false,
          fetchNextPage: () => {},
        };
      },

      useSingle: (id: string, options?: { enabled?: boolean }) => {
        const result = useQuery<GetEmployeeDetailLiteQuery>(GET_EMPLOYEE_DETAIL_LITE, {
          variables: { id },
          errorPolicy: "all",
          skip: !id,
          ...options,
        });

        return {
          ...result,
          data: result.data?.app_usersCollection?.edges?.[0]?.node || undefined,
          isLoading: result.loading,
          error: result.error || null,
          refetch: result.refetch,
        };
      },

      useUpdate: () => {
        const [mutate, result] = useMutation<UpdateUsersMutation>(UpdateUsersDocument);

        return {
          mutateAsync: async ({ id, ...input }: { id: string } & App_UsersUpdateInput) => {
            const { __typename: _typename, ...cleanInput } = input as App_UsersUpdateInput & {
              __typename?: unknown;
            };

            const { data, error } = await mutate({
              variables: { id, input: cleanInput },
              errorPolicy: "all",
              refetchQueries: ["GetUserss", "GetUsers"],
              awaitRefetchQueries: true,
            });

            if (error) throw new Error(`GraphQL error: ${error.message}`);
            const updated = data?.updateapp_usersCollection?.records?.[0];
            if (!updated) throw new Error("Failed to update employee");
            return updated as App_Users;
          },
          isPending: result.loading,
        };
      },

      useDelete: () => {
        const [mutate, result] = useMutation<DeleteUsersMutation>(DeleteUsersDocument);

        return {
          mutateAsync: async (id: string) => {
            const { data, error } = await mutate({
              variables: { id },
              errorPolicy: "all",
              refetchQueries: ["GetUserss"],
              awaitRefetchQueries: true,
            });

            if (error) throw new Error(`GraphQL error: ${error.message}`);
            const deletedId = data?.deleteFromapp_usersCollection?.records?.[0]?.id;
            if (!deletedId) throw new Error("Failed to delete employee");
            return deletedId;
          },
          isPending: result.loading,
        };
      },
      useCreate: () => {
        const [mutate, result] = useMutation<CreateUsersMutation>(CreateUsersDocument);
        return {
          mutateAsync: async (input: App_UsersInsertInput) => {
            const { __typename: _typename, id: _id, ...cleanInput } = input as App_UsersInsertInput & {
              __typename?: unknown;
            };
            const { data, error } = await mutate({
              variables: { input: cleanInput },
              errorPolicy: "all",
              refetchQueries: ["GetUserss"],
              awaitRefetchQueries: true,
            });
            if (error) throw new Error(`GraphQL error: ${error.message}`);
            const created = data?.insertIntoapp_usersCollection?.records?.[0];
            if (!created) throw new Error("Failed to create employee");
            return created as App_Users;
          },
          isPending: result.loading,
        };
      },
    }),
    []
  );
};
